import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useListComponentsQuery } from '../../api/authoring'
import { payloadId, shown } from '../../api/useIntent'
import { SchemaForm } from '../../editors/SchemaForm'
import { allLinkIds, mintLinkId } from '../../model/link'
import { LISTING_LAG } from '../../model/labels'
import type { StructuralIntent } from '../../model/manifest'
import { structuralPayload } from '../../model/manifest'
import type { Document } from '../../api/authoring'
import { EmptyState, Loading, QueryError } from '../../ui/QueryState'

/**
 * Adding a route, or editing one that exists.
 *
 * **One dialog for both, because they are the same form over different
 * intents.** `website.page_added` carries a slug, a title, a description
 * and a menu set beside the page it links; `website.route_updated`
 * carries the same four beside the page it names. Those members are the intents'
 * `rest` — everything the markers did not claim — so the form is
 * `SchemaForm` over exactly that, and neither list is written here.
 *
 * What differs is the tree-naming half. Adding needs a *child*, so it
 * offers the pages this tenant holds; editing already has one, so it does
 * not. That is read off the intent's role rather than from a flag: a
 * `place` makes a placement and an `act` names one that exists.
 *
 * **A page is linked, never inline.** A route reaches a page that a
 * person will edit on its own screen, and an inline one would have no
 * screen and no id to address — the model allows it and it would be a
 * trap, so this offers the library and the create dialog reached from it.
 */
export function RouteDialog({
  tenant,
  document,
  intent,
  route,
  under,
  position,
  onClose,
  onSend,
  busy,
}: {
  tenant: string
  document: Document
  /** The intent this dialog sends — a `place` to add, an `act` to edit. */
  intent: StructuralIntent | undefined
  /** The route's current values when editing, empty when adding. */
  route?: Record<string, unknown>
  /** The page link id of the level being added to. Empty is the top. */
  under?: string
  /** Where in that level the new route goes. */
  position?: number
  onClose: () => void
  onSend: (name: string, payload: Record<string, unknown>) => Promise<unknown>
  busy: boolean
}) {
  const adding = intent?.role === 'place'
  const [values, setValues] = useState<Record<string, unknown>>({})

  // Reset whenever the dialog is pointed at a different route, so editing
  // one and then another does not carry the first one's slug across.
  useEffect(() => setValues(route ?? {}), [route, intent?.name])

  const pages = useListComponentsQuery(
    { tenant, type: 'Page' },
    { skip: !adding || !intent },
  )

  if (!intent) return null

  const restSchema = {
    type: 'object' as const,
    properties: Object.fromEntries(intent.rest.map((property) => [property.name, property.schema])),
    required: intent.rest.filter((property) => property.required).map((property) => property.name),
  }

  // What the route cannot be sent without. `required` is not it: it says
  // the member is present, and an empty slug is present — it is the
  // route answering `/`, which content mode has always been able to set.
  // Only a stated minimum length makes empty a missing value.
  const missing = intent.rest.filter(
    (property) => String(values[property.name] ?? '').length < (property.schema.minLength ?? 0),
  )

  const send = (child?: Record<string, unknown>) =>
    onSend(
      intent.name,
      structuralPayload(
        intent,
        {
          child,
          // On a `place` the link is the *parent level*; on an `act` it is
          // the route being edited. Both arrive as `link` because the
          // marker does not distinguish them — the caller does, by
          // passing the one its operation means.
          link: adding ? under || undefined : (route?.page as string | undefined),
          position,
        },
        values,
      ),
    ).catch(shown).then?.(onClose)

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{adding ? 'Add a route' : 'Edit this route'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <SchemaForm
            schema={restSchema}
            root={restSchema}
            value={values}
            onChange={setValues}
            disabled={busy}
          />

          <Typography variant="caption" color="text.secondary">
            The slug is this route&rsquo;s own segment; the path it answers on is every slug above it joined to this
            one. Routing lives on the site, so none of this revises the page.
          </Typography>

          {adding && (
            <>
              <Divider />
              <Typography variant="subtitle2">Which page does it reach?</Typography>

              {missing.length > 0 && (
                <Alert severity="info">
                  {missing.map((property) => property.name).join(', ')} is still empty — the route needs it before a
                  page can be chosen.
                </Alert>
              )}

              {pages.isLoading && <Loading label="Listing pages…" />}
              {pages.error && <QueryError error={pages.error} title="Could not list pages" />}
              {pages.data?.components.length === 0 && (
                <EmptyState title="No pages in this tenant">
                  <Typography color="text.secondary">
                    A route reaches a page that already exists. Make one in the Page library first — it is the same
                    create the picker offers everywhere else.
                  </Typography>
                </EmptyState>
              )}

              <List dense disablePadding>
                {(pages.data?.components ?? []).map((page) => (
                  <ListItemButton
                    key={page.id}
                    disabled={busy || missing.length > 0}
                    onClick={() => {
                      const taken = new Set(allLinkIds(document.children))
                      send({ linkId: mintLinkId(page.id, taken, 'page'), id: payloadId(tenant, page.id) })
                    }}
                  >
                    <ListItemText primary={page.label ?? page.id} secondary={page.label ? page.id : undefined} />
                  </ListItemButton>
                ))}
              </List>

              <Typography variant="caption" color="text.secondary">
                {LISTING_LAG}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!adding && (
          <Button variant="contained" disabled={busy || missing.length > 0} onClick={() => send()}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

